import { useState, useEffect } from "react";
import { 
  Plus, Edit, Trash2, Eye, EyeOff, FileText, Calendar,
  Save, X, Image as ImageIcon, Loader2, ExternalLink
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Quality Management",
  "Food Safety",
  "ISO Standards",
  "Sustainability",
  "Risk & Compliance",
  "Industry News",
  "Training & Development"
];

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  
  const { getAuthHeader } = useAuth();

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "",
    featured_image: "",
    meta_title: "",
    meta_description: "",
    status: "draft"
  });

  useEffect(() => {
    fetchPosts();
  }, [filterStatus]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== "all" ? `?status=${filterStatus}` : "";
      const response = await axios.get(`${API}/blog/admin/posts${params}`, {
        headers: getAuthHeader()
      });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      excerpt: "",
      content: "",
      category: "",
      featured_image: "",
      meta_title: "",
      meta_description: "",
      status: "draft"
    });
    setEditingPost(null);
  };

  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title || "",
        excerpt: post.excerpt || "",
        content: post.content || "",
        category: post.category || "",
        featured_image: post.featured_image || "",
        meta_title: post.meta_title || "",
        meta_description: post.meta_description || "",
        status: post.status || "draft"
      });
    } else {
      resetForm();
    }
    setShowEditor(true);
  };

  const handleSave = async (publishNow = false) => {
    if (!formData.title || !formData.excerpt || !formData.content || !formData.category) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.content.length < 50) {
      toast.error("Content must be at least 50 characters");
      return;
    }

    setSaving(true);
    const payload = {
      ...formData,
      status: publishNow ? "published" : formData.status
    };

    try {
      if (editingPost) {
        await axios.patch(`${API}/blog/admin/posts/${editingPost.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Blog post updated successfully!");
      } else {
        await axios.post(`${API}/blog/admin/posts`, payload, {
          headers: getAuthHeader()
        });
        toast.success(publishNow ? "Blog post published!" : "Blog post saved as draft!");
      }
      setShowEditor(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to save blog post";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Are you sure you want to delete "${post.title}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/blog/admin/posts/${post.id}`, {
        headers: getAuthHeader()
      });
      toast.success("Blog post deleted");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete blog post");
    }
  };

  const togglePublish = async (post) => {
    const newStatus = post.status === "published" ? "draft" : "published";
    try {
      await axios.patch(`${API}/blog/admin/posts/${post.id}`, 
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(newStatus === "published" ? "Post published!" : "Post unpublished");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to update post status");
    }
  };

  const insertFormatting = (tag) => {
    const textarea = document.getElementById("content-editor");
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const selectedText = text.substring(start, end);

    let newText = "";
    let cursorOffset = 0;

    switch (tag) {
      case "bold":
        newText = `**${selectedText || "bold text"}**`;
        cursorOffset = selectedText ? newText.length : 2;
        break;
      case "italic":
        newText = `*${selectedText || "italic text"}*`;
        cursorOffset = selectedText ? newText.length : 1;
        break;
      case "heading":
        newText = `\n## ${selectedText || "Heading"}\n`;
        cursorOffset = selectedText ? newText.length : 4;
        break;
      case "link":
        newText = `[${selectedText || "link text"}](url)`;
        cursorOffset = selectedText ? newText.length - 4 : 1;
        break;
      case "list":
        newText = `\n- ${selectedText || "List item"}\n`;
        cursorOffset = selectedText ? newText.length : 3;
        break;
      default:
        return;
    }

    const updatedContent = text.substring(0, start) + newText + text.substring(end);
    setFormData({ ...formData, content: updatedContent });

    // Reset cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + cursorOffset, start + cursorOffset);
    }, 0);
  };

  // Stats
  const publishedCount = posts.filter(p => p.status === "published").length;
  const draftCount = posts.filter(p => p.status === "draft").length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-brand/10">
              <FileText className="h-5 w-5 text-brand" />
            </div>
            <div>
              <p className="text-2xl font-bold">{posts.length}</p>
              <p className="text-sm text-muted-foreground">Total Posts</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100">
              <Eye className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{publishedCount}</p>
              <p className="text-sm text-muted-foreground">Published</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <EyeOff className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{draftCount}</p>
              <p className="text-sm text-muted-foreground">Drafts</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Posts</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
          </SelectContent>
        </Select>
        <Button 
          onClick={() => openEditor()}
          className="bg-brand hover:bg-brand-dark"
          data-testid="create-post-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Blog Post
        </Button>
      </div>

      {/* Posts List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Blog Posts</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand" />
              <p className="text-muted-foreground mt-2">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Blog Posts Yet</h3>
              <p className="text-muted-foreground mb-4">
                Start sharing your insights and expertise.
              </p>
              <Button onClick={() => openEditor()} className="bg-brand hover:bg-brand-dark">
                <Plus className="h-4 w-4 mr-2" />
                Create First Post
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div 
                  key={post.id} 
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-slate-50"
                  data-testid={`blog-row-${post.id}`}
                >
                  {post.featured_image && (
                    <img 
                      src={post.featured_image} 
                      alt={post.title}
                      className="w-24 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{post.title}</h3>
                      <Badge className={post.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                        {post.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">{post.excerpt}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {post.publish_date ? new Date(post.publish_date).toLocaleDateString() : "Not published"}
                      </span>
                      <Badge variant="outline">{post.category}</Badge>
                      {post.views > 0 && <span>{post.views} views</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {post.status === "published" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => window.open(`/tools/resources/${post.slug}`, '_blank')}
                        title="View Post"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEditor(post)}
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => togglePublish(post)}
                      title={post.status === "published" ? "Unpublish" : "Publish"}
                    >
                      {post.status === "published" ? (
                        <EyeOff className="h-4 w-4 text-amber-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-green-600" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(post)}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? "Edit Blog Post" : "Create New Blog Post"}</DialogTitle>
            <DialogDescription>
              {editingPost ? "Update your blog post content" : "Share your insights and expertise"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter blog post title"
                data-testid="input-title"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Excerpt */}
            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt/Summary * (shown in previews)</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Write a brief summary (2-3 sentences)"
                rows={2}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">{formData.excerpt.length}/500 characters</p>
            </div>

            {/* Content Editor */}
            <div className="space-y-2">
              <Label>Content *</Label>
              <div className="flex gap-1 mb-2">
                <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("bold")}>
                  <strong>B</strong>
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("italic")}>
                  <em>I</em>
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("heading")}>
                  H2
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("link")}>
                  Link
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("list")}>
                  • List
                </Button>
              </div>
              <Textarea
                id="content-editor"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your blog content here... (Supports Markdown formatting)"
                rows={12}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Supports basic Markdown: **bold**, *italic*, ## Heading, [link](url), - list item
              </p>
            </div>

            {/* Featured Image */}
            <div className="space-y-2">
              <Label htmlFor="featured_image">Featured Image URL (optional)</Label>
              <Input
                id="featured_image"
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              {formData.featured_image && (
                <img 
                  src={formData.featured_image} 
                  alt="Preview" 
                  className="w-full h-32 object-cover rounded mt-2"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>

            {/* SEO Fields */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">SEO Settings (optional)</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder="Leave empty to use post title"
                    maxLength={60}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <Textarea
                    id="meta_description"
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Leave empty to use excerpt"
                    rows={2}
                    maxLength={160}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Draft
            </Button>
            <Button 
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-brand hover:bg-brand-dark"
              data-testid="publish-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingPost?.status === "published" ? "Update" : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
